// @flow

import * as React from "react";
import { type FieldRenderProps, type FieldProps, Field, FormSpy } from "react-final-form";
import cx from "classnames";
import { type FormApi } from "final-form";
import Text from "../Text";
import type { TextType } from "../TooltipContent";

import styles from "./FieldControl.scss";
import Tooltip from "../Tooltip";
import TooltipContent from "../TooltipContent";
import Error from "./Error";

export type FieldControlProps = {
  caption: ?() => React.Node,
  isOptional: boolean,
  children: FieldRenderProps => React.Node,
  additionalInfo: string,
  name: string,
  isErrorWrapped: boolean,
  link: ?() => React.Node,
  form: FormApi,
  className: string,
  validationMessage: string,
  tooltipText: Array<TextType>,
  hideCaptionSmUp: boolean,
  forceErrorPositionAbsolute: boolean,
  parse: ?(value: any) => any,
  format: ?(value: any) => any,
  validateFields: ?(value: any) => any,
  validate: ?(value: any) => any,
  customCaptionStyles?: string
};

const isFieldFilled = value => typeof value === "boolean" || typeof value === "number" || !!value;

const shouldShowError = ({ submitFailed, visited, error, validationMessage, value }) =>
  !!((submitFailed || visited || isFieldFilled(value)) && error) || !!validationMessage;

const FieldControl = ({
  caption,
  isOptional,
  children,
  name,
  validate,
  additionalInfo,
  isErrorWrapped,
  link,
  form,
  validationMessage,
  className,
  tooltipText,
  hideCaptionSmUp,
  forceErrorPositionAbsolute,
  parse,
  format,
  validateFields,
  customCaptionStyles
}: FieldControlProps & FieldProps) => (
  <div className={cx(styles.fieldControl, className)}>
    {(!!caption || isOptional || link) && (
      <span
        className={cx(styles.header, {
          [styles.hideCaptionSmUp]: hideCaptionSmUp
        })}
        data-header
      >
        {caption && <span className={cx(styles.caption, customCaptionStyles)}>{caption()}</span>}
        {isOptional && <span className={styles.optional}>optional</span>}
        {link && link()}
      </span>
    )}
    {additionalInfo && <Text additionalClasses={styles.additionalInfo}>{additionalInfo}</Text>}
    <Field
      name={name}
      validateFields={validateFields}
      validate={validate}
      parse={parse || (v => v)}
      format={format || (v => v)}
    >
      {({ input, meta, meta: { error, visited } }: FieldRenderProps) => (
        <>
          <div className={styles.inputContainer}>
            {children({ input, meta })}
            <div className={styles.tooltipContainer}>
              {tooltipText && tooltipText.length > 0 && (
                <Tooltip content={() => <TooltipContent texts={tooltipText} />} />
              )}
            </div>
          </div>
          <Error
            text={error || validationMessage}
            isVisible={shouldShowError({
              submitFailed: form.getState().submitFailed,
              visited,
              error,
              validationMessage,
              value: input.value
            })}
            isWrappingForced={isErrorWrapped}
            forceErrorPositionAbsolute={forceErrorPositionAbsolute}
          />
        </>
      )}
    </Field>
  </div>
);

const FieldControlWrapper = (props: FieldControlProps) => (
  <FormSpy
    subscription={{
      submitError: true,
      submitFailed: true,
      submitSucceeded: true
    }}
    render={({ form }) => <FieldControl {...props} form={form} />}
  />
);

FieldControl.defaultProps = {
  name: "empty",
  caption: null,
  isOptional: false,
  additionalInfo: "",
  isErrorWrapped: false,
  link: null,
  tooltipText: [],
  hideCaptionSmUp: false,
  forceErrorPositionAbsolute: false,
  parse: null,
  format: null,
  validateFields: undefined
};

export default FieldControlWrapper;
